import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Gavel } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, formatDateOnly } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

type Status = "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";

interface LegalAction {
  id: string;
  title: string;
  description: string;
  relatedUnit?: string | null;
  assignedTo?: string | null;
  status: Status;
  dueDate?: string | null;
  resolution?: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_FLOW: Status[] = ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"];

const statusVariant: Record<Status, "warning" | "info" | "success" | "default"> = {
  OPEN: "warning",
  IN_PROGRESS: "info",
  RESOLVED: "success",
  CLOSED: "default",
};

export function LegalActionsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editAction, setEditAction] = useState<LegalAction | null>(null);

  const { data: actions = [], isLoading, isError } = useQuery({
    queryKey: ["legal-actions"],
    queryFn: async () => (await api.get<LegalAction[]>("/legal-actions")).data,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status, resolution }: { id: string; status: Status; resolution?: string }) =>
      (await api.put(`/legal-actions/${id}`, { status, resolution })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["legal-actions"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/legal-actions/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["legal-actions"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Gavel size={24} /> {t("legal.legalActions")}
        </h1>
        <button onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} /> {t("legal.newAction")}
        </button>
      </div>

      {isError && <QueryError />}

      {/* Status board */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {STATUS_FLOW.map((status) => {
          const items = actions.filter((a) => a.status === status);
          return (
            <div key={status} className="bg-slate-100 rounded-lg p-3">
              <div className="font-semibold text-slate-700 mb-2 flex items-center justify-between">
                <span>{t(`legal.legalStatus.${status}`)}</span>
                <Badge variant={statusVariant[status]}>{items.length}</Badge>
              </div>
              <div className="space-y-2">
                {isLoading && status === "OPEN" && <div className="text-xs text-slate-500">{t("common.loading")}</div>}
                {items.map((a) => (
                  <div key={a.id} className="bg-white rounded p-3 shadow-sm">
                    <div className="font-medium text-sm">{a.title}</div>
                    {a.relatedUnit && <div className="text-xs text-slate-500 mt-1">Unidad {a.relatedUnit}</div>}
                    <p className="text-xs text-slate-600 mt-1 line-clamp-3">{a.description}</p>
                    {a.assignedTo && <div className="text-xs text-slate-500 mt-1">→ {a.assignedTo}</div>}
                    {a.dueDate && <div className="text-xs text-amber-700 mt-1">{t("legal.dueDate")}: {formatDateOnly(a.dueDate, i18n.language)}</div>}
                    {a.resolution && <div className="text-xs text-green-700 mt-1 italic">{a.resolution}</div>}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {STATUS_FLOW.filter((s) => s !== a.status).map((s) => (
                        <button
                          key={s}
                          className="text-xs text-primary hover:underline"
                          onClick={() => {
                            if (s === "RESOLVED" || s === "CLOSED") {
                              const r = prompt(t("legal.resolution") + ":");
                              updateMutation.mutate({ id: a.id, status: s, resolution: r ?? undefined });
                            } else {
                              updateMutation.mutate({ id: a.id, status: s });
                            }
                          }}
                        >
                          → {t(`legal.legalStatus.${s}`)}
                        </button>
                      ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <AdminRecordActions
                        compact
                        onEdit={() => setEditAction(a)}
                        onDelete={() => deleteMutation.mutate(a.id)}
                        entityLabel="la acción legal"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <NewActionModal open={open} onClose={() => setOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["legal-actions"] })} />
      {editAction && (
        <NewActionModal
          key={editAction.id}
          open
          action={editAction}
          onClose={() => setEditAction(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ["legal-actions"] })}
        />
      )}
    </div>
  );
}

function NewActionModal({ open, action, onClose, onDone }: { open: boolean; action?: LegalAction | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const isEdit = !!action;
  const [form, setForm] = useState({
    title: action?.title ?? "",
    description: action?.description ?? "",
    relatedUnit: action?.relatedUnit ?? "",
    assignedTo: action?.assignedTo ?? "",
    dueDate: action?.dueDate ? action.dueDate.slice(0, 10) : "",
  });
  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        relatedUnit: form.relatedUnit || null,
        assignedTo: form.assignedTo || null,
        dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,
      };
      if (isEdit && action) return (await api.put(`/legal-actions/${action.id}`, body)).data;
      return (await api.post("/legal-actions", body)).data;
    },
    onSuccess: () => {
      onDone();
      onClose();
      if (!isEdit) setForm({ title: "", description: "", relatedUnit: "", assignedTo: "", dueDate: "" });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t("common.edit") : t("legal.newAction")} size="lg">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <div>
          <label className="label">Título</label>
          <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label className="label">Descripción</label>
          <textarea className="input min-h-[100px]" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">{t("legal.relatedUnit")}</label>
            <input className="input" value={form.relatedUnit} onChange={(e) => setForm({ ...form, relatedUnit: e.target.value })} />
          </div>
          <div>
            <label className="label">Asignado a</label>
            <input className="input" value={form.assignedTo} onChange={(e) => setForm({ ...form, assignedTo: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("legal.dueDate")}</label>
            <input type="date" className="input" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
